import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { apiGetAllZones } from "api/zones";
import { apiGetAllCategories } from "api/categories";
import { searchBoxStyles } from "utils/pageHeaderStyles";

const PenyewaSearch = ({
  search,
  handleSearchChange,
  onTambah,
  onEdit,
  onDelete,
  onPrint,
  onColumnMenuOpen,
  selectedRow,
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode, whiteSidenav, transparentSidenav } = controller;
  const shouldApplyStyle =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  // State for dropdown data
  const [zones, setZones] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch zones and categories on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await apiGetAllZones(1, 100); // Get first 100 zones
        if (response.data) {
          setZones(response.data);
        }
      } catch (error) {
        console.error("Error fetching zones:", error);
        setZones([]);
      } finally {
        setLoadingZones(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await apiGetAllCategories(1, 100); // Get first 100 categories
        if (response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchZones();
    fetchCategories();
  }, []);

  return (
    <MDBox sx={searchBoxStyles.container}>
      <Typography variant="h6" fontWeight="bold" mb={1.5}>
        Carian
      </Typography>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            label="Nama"
            name="nama"
            size="small"
            value={search.nama}
            onChange={handleSearchChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Kod Pelanggan"
            name="kod_pelanggan"
            size="small"
            value={search.kod_pelanggan}
            onChange={handleSearchChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="No Akaun"
            name="no_akaun"
            size="small"
            value={search.no_akaun}
            onChange={handleSearchChange}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} alignItems="center" mt={0.25}>
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            size="small"
            sx={searchBoxStyles.dropdownField}
           >
            <InputLabel id="zon-label">Zon</InputLabel>
            <Select
              labelId="zon-label"
              id="zon"
              label="Zon"
              name="zon"
              value={search.zon}
              onChange={handleSearchChange}
              fullWidth
            >
              <MenuItem value="">
                <em>Pilih Zon</em>
              </MenuItem>
              {loadingZones ? (
                <MenuItem value="" disabled>
                  Loading...
                </MenuItem>
              ) : (
                zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.zon}>
                    {zone.zon}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            size="small"
            sx={searchBoxStyles.dropdownField}
           >
            <InputLabel id="kategori-kutipan-label">Kategori Kutipan</InputLabel>
            <Select
              labelId="kategori-kutipan-label"
              label="Kategori Kutipan"
              name="kategori_kutipan"
              value={search.kategori_kutipan}
              onChange={handleSearchChange}
              fullWidth
            >
              <MenuItem value="">
                <em>Pilih Kategori</em>
              </MenuItem>
              {loadingCategories ? (
                <MenuItem value="" disabled>
                  Loading...
                </MenuItem>
              ) : (
                categories.map((category) => (
                  <MenuItem key={category.id} value={category.kategori_kutipan}>
                    {category.kategori_kutipan}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl
            fullWidth
            size="small"
            sx={searchBoxStyles.dropdownField}
           >
            <InputLabel id="status-akaun-label">Status Akaun</InputLabel>
            <Select
              labelId="status-akaun-label"
              id="status-akaun"
              label="Status Akaun"
              name="status_akaun"
              value={search.status_akaun}
              onChange={handleSearchChange}
              fullWidth
            >
              <MenuItem value="">
                <em>Pilih Status</em>
              </MenuItem>
              <MenuItem value="Aktif">Aktif</MenuItem>
              <MenuItem value="Tidak Aktif">Tidak Aktif</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} alignItems="center" mt={1}>
        <Button
          sx={{ fontSize: { xs: 10, sm: 10, md: 10, lg: 10 } }}
          variant="text"
          color="success"
          startIcon={<AddCircleIcon />}
          size="small"
          onClick={onTambah}
        >
          Tambah Rekod
        </Button>
        <Button
          sx={{ fontSize: { xs: 10, sm: 10, md: 10, lg: 10 } }}
          variant="text"
          color="info"
          startIcon={<EditIcon />}
          size="small"
          onClick={onEdit}
          disabled={!selectedRow}
        >
          Ubahsuai Rekod
        </Button>
        <Button
          sx={{ fontSize: { xs: 10, sm: 10, md: 10, lg: 10 } }}
          variant="text"
          color="error"
          startIcon={<DeleteIcon />}
          size="small"
          onClick={onDelete}
          disabled={!selectedRow}
        >
          Hapus Rekod
        </Button>
        <Button
          sx={{ fontSize: { xs: 10, sm: 10, md: 10, lg: 10 } }}
          variant="text"
          color="success"
          startIcon={<PrintIcon />}
          size="small"
          onClick={onPrint}
        >
          Cetak (By Zon & Status Akaun)
        </Button>
        <Button
          sx={{ fontSize: { xs: 10, sm: 10, md: 10, lg: 10 } }}
          variant="text"
          color="success"
          startIcon={<ViewColumnIcon />}
          size="small"
          onClick={onColumnMenuOpen}
        >
          Pilihan Lajur
        </Button>
      </Stack>
    </MDBox>
  );
};

PenyewaSearch.propTypes = {
  search: PropTypes.shape({
    nama: PropTypes.string,
    no_kp: PropTypes.string,
    kod_pelanggan: PropTypes.string,
    no_akaun: PropTypes.string,
    zon: PropTypes.string,
    kategori_kutipan: PropTypes.string,
    status_akaun: PropTypes.string,
  }).isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  onTambah: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onPrint: PropTypes.func,
  onColumnMenuOpen: PropTypes.func,
  selectedRow: PropTypes.object,
};

PenyewaSearch.defaultProps = {
  onTambah: () => {},
  onEdit: () => {},
  onDelete: () => {},
  onPrint: () => {},
  onColumnMenuOpen: () => {},
  selectedRow: null,
};

export default PenyewaSearch;
